import { useEffect, useMemo, useState } from "react";

const MOBILE_MEDIA_QUERY = "(max-width: 768px)";
const STAR_COUNT_DESKTOP = 280;
const STAR_COUNT_MOBILE = 110;

function GalaxyBackground({ settings }) {
  const [isMobile, setIsMobile] = useState(false);
  const starDensity = settings?.starDensity ?? 1;
  const starSpeed = settings?.starSpeed ?? 1;
  const twinkleIntensity = settings?.twinkleIntensity ?? 1;
  const nebulaIntensity = settings?.nebulaIntensity ?? 1;
  const effectIntensity = settings?.effectIntensity ?? 1;
  const effectMode = settings?.effectMode || "classic";

  useEffect(() => {
    const media = window.matchMedia(MOBILE_MEDIA_QUERY);
    const apply = () => setIsMobile(media.matches);
    apply();

    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, []);

  const stars = useMemo(
    () => {
      const baseCount = isMobile ? STAR_COUNT_MOBILE : STAR_COUNT_DESKTOP;
      const resolvedCount = Math.max(30, Math.floor(baseCount * starDensity));
      return Array.from({ length: resolvedCount }, (_, index) => {
        const seed = (index * 37) % 100;
        return {
          id: index,
          left: `${(seed * 1.7) % 100}%`,
          top: `${(index * 9.3) % 100}%`,
          size: (isMobile ? 1.6 : 2) + ((index * 11) % 4) * (isMobile ? 0.8 : 1),
          drift: ((isMobile ? 4.2 : 3.2) + (index % 5) * 0.85) / starSpeed,
          twinkle: ((isMobile ? 0.9 : 0.6) + (index % 4) * 0.25) / twinkleIntensity,
          delay: -((index * 0.37) % 8),
          opacity: 0.42 + ((index * 13) % 45) / 100,
        };
      });
    },
    [isMobile, starDensity, starSpeed, twinkleIntensity]
  );

  const waveLines = useMemo(
    () =>
      Array.from({ length: isMobile ? 8 : 14 }, (_, index) => ({
        id: index,
        width: 32 + ((index * 11) % 58),
        delay: -((index * 0.28) % 3),
        duration: 2.2 + (index % 5) * 0.4,
      })),
    [isMobile]
  );

  const windLines = useMemo(
    () =>
      Array.from({ length: isMobile ? 18 : 30 }, (_, index) => ({
        id: index,
        top: `${(index * 7.1) % 100}%`,
        width: `${24 + ((index * 13) % 56)}px`,
        delay: -((index * 0.41) % 5),
        duration: (2.3 + (index % 4) * 0.55) / starSpeed,
      })),
    [isMobile, starSpeed]
  );

  return (
    <div
      className={`galaxy-bg effect-${effectMode}`}
      style={{
        "--nebula-intensity": `${nebulaIntensity}`,
        "--effect-intensity": `${effectIntensity}`,
      }}
      aria-hidden="true"
    >
      <div className="nebula nebula-a" />
      <div className="nebula nebula-b" />
      <div className="nebula nebula-c" />
      <div className="orbit-core">
        <span className="orbit-ring ring-a" />
        <span className="orbit-ring ring-b" />
        <span className="orbit-ring ring-c" />
      </div>
      <div className="quantum-grid-overlay" />
      <div className="tech-grid" />

      <div className="cosmic-wave">
        {waveLines.map((wave) => (
          <span
            key={wave.id}
            className="wave-line"
            style={{
              width: `${wave.width}%`,
              animationDelay: `${wave.delay}s`,
              animationDuration: `${wave.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="solar-wind">
        {windLines.map((line) => (
          <span
            key={line.id}
            className="wind-line"
            style={{
              top: line.top,
              width: line.width,
              animationDelay: `${line.delay}s`,
              animationDuration: `${line.duration}s`,
            }}
          />
        ))}
      </div>

      <div className="starfield">
        {stars.map((star) => (
          <span
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animationDuration: `${star.twinkle}s, ${star.drift}s`,
              animationDelay: `${star.delay}s, ${star.delay}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export default GalaxyBackground;
